import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import {
  Badge,
  Button,
  Checkbox,
  Group,
  Modal,
  NativeSelect,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Textarea,
} from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { api } from '@/lib/api'
import { useOnShiftUsers } from '@/lib/on-shift-users-context'
import type {
  CurrentShiftResponse,
  SupportKnowledgeChangeRow,
  SupportKnowledgeTrainingPendingProject,
} from '@/types/api'
import { getShiftLocalState, setShiftLocalState, SHIFT_STATE_KEY } from '@/lib/shift-local-state'
import { parseApiDateUtc } from '@/lib/api-date'
import { formatShiftStartedAtForDisplay } from '@/lib/user-datetime-format'
import { formatDateTimeDot } from '@/lib/ticket-display'

/** Фоновая сверка с API (один источник правды для всех экранов) */
const SYNC_MS = 30_000
/** Обновление текста «прошло N мин» в UI */
const DISPLAY_TICK_MS = 15_000
const KNOWLEDGE_CHANGES_PAGE_SIZE = 5

const KNOWLEDGE_CHANGE_LABELS: Record<string, string> = {
  created: 'Добавлено',
  updated: 'Обновлено',
  deleted: 'Удалено',
  attachment_added: 'Вложение добавлено',
  attachment_deleted: 'Вложение удалено',
}

function formatMyPlanLines(backend: CurrentShiftResponse | null): {
  primary: string | null
  detail: string | null
} {
  if (!backend) return { primary: null, detail: null }
  const minutes = backend.planned_shift_minutes_today
  const label = (backend.planned_intervals_label || '').trim() || null
  const start = backend.planned_start_local
  const end = backend.planned_end_local
  const hasPlan =
    (typeof minutes === 'number' && minutes > 0) || !!(label || start || end)
  if (!hasPlan) {
    return { primary: 'В графике на сегодня смены нет', detail: null }
  }
  let primary: string | null = null
  if (start && end) primary = `По плану ${start}–${end}`
  else if (end) primary = `По плану до ${end}`
  else if (start) primary = `По плану с ${start}`
  else if (label) primary = `По плану: ${label}`
  const detail =
    label && primary && !primary.includes(label) ? label : null
  return { primary, detail }
}

function formatShiftInfo(active: boolean, startedAt: string | null): string {
  if (!active || !startedAt) return 'Смена не начата'
  const started = parseApiDateUtc(startedAt)
  if (!started) return 'Смена не начата'
  const now = new Date()
  let diffMs = now.getTime() - started.getTime()
  if (diffMs < 0) diffMs = 0
  const totalMinutes = Math.floor(diffMs / 60000)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  const startedStr = formatShiftStartedAtForDisplay(startedAt) ?? '—'
  const durationStr = hours > 0 ? `${hours} ч ${minutes} мин` : `${minutes} мин`
  return `Смена с ${startedStr}, прошло ${durationStr}`
}

function applyBackendToLocal(backend: CurrentShiftResponse) {
  const backendActive = !!backend.active
  const backendStartedAt = backend.started_at || null
  const local = getShiftLocalState()
  let next = { ...local }
  if (backendActive) {
    if (!local.active || !local.startedAt || local.startedAt !== backendStartedAt) {
      next = { active: true, startedAt: backendStartedAt }
    }
  } else if (local.active || local.startedAt) {
    next = { active: false, startedAt: null }
  }
  setShiftLocalState(next)
  return next
}

export type SidebarShiftValue = {
  shift: { active: boolean; startedAt: string | null }
  shiftInfo: string
  planPrimary: string | null
  planDetail: string | null
  shiftBackend: CurrentShiftResponse | null
  busy: boolean
  showBreak: boolean
  breakLabel: string
  breakInfo: string
  isOnBreak: boolean
  refreshFromBackend: () => Promise<void>
  toggleShift: () => Promise<void>
  toggleBreak: () => Promise<void>
}

type ShiftContextValue = SidebarShiftValue & {
  subscribeMutationSuccess: (cb: () => void) => () => void
}

const ShiftContext = createContext<ShiftContextValue | null>(null)

export function ShiftProvider({ children }: { children: ReactNode }) {
  const { refresh: refreshOnShiftIds } = useOnShiftUsers()
  const [shift, setShift] = useState(() => getShiftLocalState())
  const [backend, setBackend] = useState<CurrentShiftResponse | null>(null)
  const [busy, setBusy] = useState(false)
  const [knowledgeChanges, setKnowledgeChanges] = useState<SupportKnowledgeChangeRow[]>([])
  const [knowledgeChangesTotal, setKnowledgeChangesTotal] = useState(0)
  const [knowledgeViewId, setKnowledgeViewId] = useState<string | null>(null)
  const [knowledgeModalOpened, setKnowledgeModalOpened] = useState(false)
  const [knowledgePage, setKnowledgePage] = useState(1)
  const [knowledgeAckBusy, setKnowledgeAckBusy] = useState(false)
  const knowledgeModalOpenedAt = useRef<number | null>(null)
  const [trainingProjects, setTrainingProjects] = useState<SupportKnowledgeTrainingPendingProject[]>([])
  const [trainingIndex, setTrainingIndex] = useState(0)
  const [trainingAnswers, setTrainingAnswers] = useState<Record<string, string | string[]>>({})
  const [trainingBusy, setTrainingBusy] = useState(false)
  const [trainingResultText, setTrainingResultText] = useState<string | null>(null)
  const syncInFlight = useRef(false)
  const mutationListeners = useRef(new Set<() => void>())

  const subscribeMutationSuccess = useCallback((cb: () => void) => {
    mutationListeners.current.add(cb)
    return () => {
      mutationListeners.current.delete(cb)
    }
  }, [])

  const notifyMutationSuccess = useCallback(() => {
    for (const fn of mutationListeners.current) {
      try {
        fn()
      } catch {
        /* ignore */
      }
    }
  }, [])

  const refreshFromBackend = useCallback(async () => {
    if (syncInFlight.current) return
    syncInFlight.current = true
    try {
      const data = await api.currentShift()
      setBackend(data)
      const next = applyBackendToLocal(data)
      setShift(next)
    } catch {
      /* офлайн — оставляем локальное */
    } finally {
      syncInFlight.current = false
    }
  }, [])

  useEffect(() => {
    void refreshFromBackend()
  }, [refreshFromBackend])

  useEffect(() => {
    const id = window.setInterval(() => void refreshFromBackend(), SYNC_MS)
    return () => clearInterval(id)
  }, [refreshFromBackend])

  const [, setDisplayTick] = useState(0)
  useEffect(() => {
    const id = window.setInterval(() => setDisplayTick((t) => t + 1), DISPLAY_TICK_MS)
    return () => clearInterval(id)
  }, [])

  /** Та же вкладка: финальное состояние после API (не срабатывает на оптимистичный save). */
  useEffect(() => {
    const onCustom = (e: Event) => {
      const ce = e as CustomEvent<{ active?: boolean; startedAt?: string | null }>
      const d = ce.detail
      if (d && typeof d.active === 'boolean') {
        setShift({ active: d.active, startedAt: d.startedAt ?? null })
      } else {
        setShift(getShiftLocalState())
      }
      void refreshFromBackend()
    }
    window.addEventListener('servicedesk:shift-changed', onCustom)
    return () => window.removeEventListener('servicedesk:shift-changed', onCustom)
  }, [refreshFromBackend])

  /** Другая вкладка браузера — там нет CustomEvent, только storage. */
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== SHIFT_STATE_KEY) return
      setShift(getShiftLocalState())
      void refreshFromBackend()
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [refreshFromBackend])

  const shiftInfo = formatShiftInfo(shift.active, shift.startedAt)
  const planLines = useMemo(() => formatMyPlanLines(backend), [backend])

  const showBreak =
    !!backend?.active &&
    ((!!backend?.break_enabled && !!backend?.can_show_break_button) || !!backend?.is_on_break)

  const breakLabel = backend?.is_on_break ? 'Закончить перерыв' : 'Перерыв'

  let breakInfo = ''
  if (backend?.active && backend?.break_enabled) {
    const total = Number(backend.total_break_minutes_today) || 0
    const max = Number(backend.break_max_minutes_today) || 0
    breakInfo = max > 0 ? `Перерыв: ${total} мин / ${max} мин` : `Перерыв: ${total} мин`
  }

  const loadKnowledgeChangesForShift = useCallback(async () => {
    try {
      const data = await api.supportKnowledgePendingChanges(300)
      if (!data.enabled || !Array.isArray(data.changes) || data.changes.length === 0) {
        return
      }
      setKnowledgeChanges(data.changes)
      setKnowledgeChangesTotal(data.total || data.changes.length)
      setKnowledgeViewId(data.view_id || null)
      setKnowledgePage(1)
      knowledgeModalOpenedAt.current = Date.now()
      setKnowledgeModalOpened(true)
    } catch {
      /* Ознакомление не должно блокировать старт смены. */
    }
  }, [])

  const finishKnowledgeChangesView = useCallback(async (action: 'closed' | 'acknowledged') => {
    if (knowledgeAckBusy) return
    setKnowledgeAckBusy(true)
    const openedAt = knowledgeModalOpenedAt.current
    const durationSeconds = openedAt ? Math.max(0, Math.round((Date.now() - openedAt) / 1000)) : null
    const pageCount = Math.max(
      1,
      Math.ceil(knowledgeChanges.length / KNOWLEDGE_CHANGES_PAGE_SIZE),
    )
    const currentPage = Math.min(knowledgePage, pageCount)
    try {
      if (knowledgeViewId) {
        await api.supportKnowledgeFinishChangeView({
          view_id: knowledgeViewId,
          action,
          last_page_seen: currentPage,
          total_pages: pageCount,
          duration_seconds: durationSeconds,
        })
      } else if (action === 'acknowledged') {
        await api.supportKnowledgeAcknowledgeChanges()
      }
      setKnowledgeModalOpened(false)
      setKnowledgeViewId(null)
      setKnowledgeChanges([])
      setKnowledgeChangesTotal(0)
      setKnowledgePage(1)
      knowledgeModalOpenedAt.current = null
    } catch (e) {
      showNotification({
        color: 'red',
        title: 'База знаний',
        message: e instanceof Error ? e.message : 'Не удалось сохранить действие',
      })
    } finally {
      setKnowledgeAckBusy(false)
    }
  }, [knowledgeAckBusy, knowledgeChanges.length, knowledgePage, knowledgeViewId])

  const loadTrainingForShift = useCallback(async () => {
    try {
      const data = await api.supportKnowledgeTrainingPending()
      if (!data.enabled || !Array.isArray(data.projects) || data.projects.length === 0) return
      setTrainingProjects(data.projects)
      setTrainingIndex(0)
      setTrainingAnswers({})
      setTrainingResultText(null)
    } catch {
      /* Проверка знаний не должна ломать старт смены при сетевой ошибке. */
    }
  }, [])

  const submitTrainingProject = useCallback(async () => {
    const project = trainingProjects[trainingIndex]
    if (!project || trainingBusy) return
    setTrainingBusy(true)
    try {
      const result = await api.supportKnowledgeTrainingSubmitAttempt({
        project_id: project.project_id,
        answers: project.questions.map((q) => ({
          question_id: q.id,
          answer: trainingAnswers[q.id] ?? null,
        })),
      })
      const statusText =
        result.status === 'passed'
          ? 'Проверка пройдена.'
          : result.status === 'failed'
            ? 'Проверка не пройдена. Попробуйте пройти еще раз.'
            : 'Ответы отправлены на ручную проверку.'
      setTrainingResultText(`${statusText} Результат: ${result.score} / ${result.max_score}.`)
      if (result.status !== 'failed') {
        const nextIndex = trainingIndex + 1
        if (nextIndex >= trainingProjects.length) {
          setTrainingProjects([])
          setTrainingIndex(0)
          setTrainingAnswers({})
        } else {
          setTrainingIndex(nextIndex)
          setTrainingAnswers({})
        }
      }
    } catch (e) {
      showNotification({
        color: 'red',
        title: 'Проверка знаний',
        message: e instanceof Error ? e.message : 'Не удалось отправить ответы',
      })
    } finally {
      setTrainingBusy(false)
    }
  }, [trainingAnswers, trainingBusy, trainingIndex, trainingProjects])

  const toggleShift = useCallback(async () => {
    if (busy) return
    const current = getShiftLocalState()
    const nowIso = new Date().toISOString()
    const optimistic = current.active
      ? { active: false, startedAt: null as string | null }
      : { active: true, startedAt: nowIso }
    setShift(optimistic)
    setShiftLocalState(optimistic, false)
    setBusy(true)
    try {
      if (optimistic.active) {
        await api.startShift()
        const next = await api.currentShift()
        setBackend(next)
        const synced = applyBackendToLocal(next)
        setShift(synced)
        void loadKnowledgeChangesForShift()
        void loadTrainingForShift()
      } else {
        await api.endShift()
        const next = await api.currentShift()
        setBackend(next)
        const synced = applyBackendToLocal(next)
        setShift(synced)
      }
      notifyMutationSuccess()
      void refreshOnShiftIds()
    } catch (e) {
      setShift(current)
      setShiftLocalState(current, false)
      void refreshFromBackend()
      showNotification({
        color: 'red',
        title: 'Смена',
        message: e instanceof Error ? e.message : 'Не удалось изменить смену',
      })
    } finally {
      setBusy(false)
    }
  }, [busy, loadKnowledgeChangesForShift, loadTrainingForShift, notifyMutationSuccess, refreshFromBackend, refreshOnShiftIds])

  const toggleBreak = useCallback(async () => {
    if (busy) return
    setBusy(true)
    try {
      const cur = await api.currentShift()
      const data = cur
      if (data.is_on_break) {
        const endRes = await api.endBreak()
        if (endRes.break_limit_exceeded) {
          const cleared = { active: false, startedAt: null as string | null }
          setShift(cleared)
          setShiftLocalState(cleared)
        }
      } else {
        await api.startBreak()
      }
      const next = await api.currentShift()
      setBackend(next)
      const synced = applyBackendToLocal(next)
      setShift(synced)
      notifyMutationSuccess()
      void refreshOnShiftIds()
    } catch (e) {
      showNotification({
        color: 'red',
        title: 'Перерыв',
        message: e instanceof Error ? e.message : 'Не удалось изменить перерыв',
      })
    } finally {
      setBusy(false)
    }
  }, [busy, notifyMutationSuccess, refreshOnShiftIds])

  const value = useMemo<ShiftContextValue>(
    () => ({
      shift,
      shiftInfo,
      planPrimary: planLines.primary,
      planDetail: planLines.detail,
      shiftBackend: backend,
      busy,
      showBreak,
      breakLabel,
      breakInfo,
      isOnBreak: !!backend?.is_on_break,
      refreshFromBackend,
      toggleShift,
      toggleBreak,
      subscribeMutationSuccess,
    }),
    [
      shift,
      shiftInfo,
      planLines.primary,
      planLines.detail,
      backend,
      busy,
      showBreak,
      breakLabel,
      breakInfo,
      refreshFromBackend,
      toggleShift,
      toggleBreak,
      subscribeMutationSuccess,
    ],
  )

  const knowledgePageCount = Math.max(
    1,
    Math.ceil(knowledgeChanges.length / KNOWLEDGE_CHANGES_PAGE_SIZE),
  )
  const safeKnowledgePage = Math.min(knowledgePage, knowledgePageCount)
  const visibleKnowledgeChanges = knowledgeChanges.slice(
    (safeKnowledgePage - 1) * KNOWLEDGE_CHANGES_PAGE_SIZE,
    safeKnowledgePage * KNOWLEDGE_CHANGES_PAGE_SIZE,
  )

  return (
    <ShiftContext.Provider value={value}>
      {children}
      <Modal
        opened={knowledgeModalOpened}
        onClose={() => void finishKnowledgeChangesView('closed')}
        closeOnClickOutside={false}
        closeOnEscape={false}
        withCloseButton
        centered
        size="760px"
        title="Изменения в базе знаний"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Перед началом работы ознакомьтесь с обновлениями. После подтверждения они больше не
            будут показываться при старте смены.
          </Text>

          <ScrollArea.Autosize mah={420} type="auto" offsetScrollbars>
            <Stack gap="sm" pr={4}>
              {visibleKnowledgeChanges.map((item) => (
                <Paper key={item.id} withBorder radius="md" p="sm">
                  <Stack gap={6}>
                    <Group gap={6} justify="space-between" align="flex-start" wrap="nowrap">
                      <Text fw={700} size="sm" style={{ minWidth: 0 }}>
                        {item.title}
                      </Text>
                      <Badge size="sm" variant="light">
                        {KNOWLEDGE_CHANGE_LABELS[item.change_type] || item.change_type}
                      </Badge>
                    </Group>
                    <Group gap={6}>
                      <Badge size="xs" variant="outline" color={item.project_id ? 'brand' : 'gray'}>
                        {item.project_name || 'Общие'}
                      </Badge>
                      {item.material_type_label ? (
                        <Badge size="xs" variant="light" color="gray">
                          {item.material_type_label}
                        </Badge>
                      ) : null}
                    </Group>
                    {item.summary ? (
                      <Text size="sm">{item.summary}</Text>
                    ) : null}
                    <Text size="xs" c="dimmed">
                      {formatDateTimeDot(item.created_at)}
                      {item.changed_by_name ? ` · ${item.changed_by_name}` : ''}
                    </Text>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </ScrollArea.Autosize>

          <Group justify="space-between" align="center">
            <Text size="xs" c="dimmed">
              Показано {visibleKnowledgeChanges.length} из {knowledgeChangesTotal}
              {knowledgePageCount > 1 ? ` · страница ${safeKnowledgePage} из ${knowledgePageCount}` : ''}
            </Text>
            <Group gap="xs">
              <Button
                variant="default"
                loading={knowledgeAckBusy}
                onClick={() => void finishKnowledgeChangesView('closed')}
              >
                Закрыть
              </Button>
              <Button
                variant="default"
                disabled={safeKnowledgePage <= 1}
                onClick={() => setKnowledgePage((p) => Math.max(1, p - 1))}
              >
                Назад
              </Button>
              {safeKnowledgePage < knowledgePageCount ? (
                <Button onClick={() => setKnowledgePage((p) => Math.min(knowledgePageCount, p + 1))}>
                  Далее
                </Button>
              ) : (
                <Button loading={knowledgeAckBusy} onClick={() => void finishKnowledgeChangesView('acknowledged')}>
                  Я ознакомился
                </Button>
              )}
            </Group>
          </Group>
        </Stack>
      </Modal>
      <Modal
        opened={trainingProjects.length > 0}
        onClose={() => {
          const project = trainingProjects[trainingIndex]
          if (project?.block_if_due) return
          setTrainingProjects([])
          setTrainingIndex(0)
          setTrainingAnswers({})
        }}
        closeOnClickOutside={false}
        closeOnEscape={false}
        withCloseButton={!trainingProjects[trainingIndex]?.block_if_due}
        centered
        size="760px"
        title="Проверка знаний"
      >
        {trainingProjects[trainingIndex] ? (
          <Stack gap="md">
            <Stack gap={2}>
              <Text fw={700}>{trainingProjects[trainingIndex].project_name}</Text>
              <Text size="sm" c="dimmed">
                Порог допуска: {trainingProjects[trainingIndex].threshold_percent}%.
                {trainingProjects[trainingIndex].block_if_due
                  ? ' Проверка обязательна перед началом работы по проекту.'
                  : ' Проверку можно пройти позже.'}
              </Text>
            </Stack>
            {trainingResultText ? (
              <Paper withBorder radius="md" p="sm">
                <Text size="sm">{trainingResultText}</Text>
              </Paper>
            ) : null}
            <ScrollArea.Autosize mah={460} type="auto" offsetScrollbars>
              <Stack gap="sm" pr={4}>
                {trainingProjects[trainingIndex].questions.map((q, idx) => (
                  <Paper key={q.id} withBorder radius="md" p="sm">
                    <Stack gap="xs">
                      <Group justify="space-between" align="flex-start" wrap="nowrap">
                        <Text fw={600} size="sm">
                          {idx + 1}. {q.question_text}
                        </Text>
                        <Badge size="sm" variant="light">{q.points} б.</Badge>
                      </Group>
                      {q.question_type === 'free_text' ? (
                        <Textarea
                          placeholder="Введите ответ"
                          minRows={3}
                          value={String(trainingAnswers[q.id] || '')}
                          onChange={(e) =>
                            setTrainingAnswers((prev) => ({ ...prev, [q.id]: e.currentTarget.value }))
                          }
                        />
                      ) : q.question_type === 'single_choice' ? (
                        <NativeSelect
                          value={String(trainingAnswers[q.id] || '')}
                          onChange={(e) =>
                            setTrainingAnswers((prev) => ({ ...prev, [q.id]: e.currentTarget.value }))
                          }
                          data={[
                            { value: '', label: 'Выберите ответ' },
                            ...(q.options || []).map((option) => ({ value: option, label: option })),
                          ]}
                        />
                      ) : (
                        <Stack gap={4}>
                          {(q.options || []).map((option) => {
                            const selected = Array.isArray(trainingAnswers[q.id])
                              ? (trainingAnswers[q.id] as string[])
                              : []
                            return (
                              <Checkbox
                                key={option}
                                label={option}
                                checked={selected.includes(option)}
                                onChange={(e) => {
                                  const checked = e.currentTarget.checked
                                  setTrainingAnswers((prev) => {
                                    const current = Array.isArray(prev[q.id]) ? (prev[q.id] as string[]) : []
                                    return {
                                      ...prev,
                                      [q.id]: checked
                                        ? Array.from(new Set([...current, option]))
                                        : current.filter((x) => x !== option),
                                    }
                                  })
                                }}
                              />
                            )
                          })}
                        </Stack>
                      )}
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </ScrollArea.Autosize>
            <Group justify="space-between">
              <Text size="xs" c="dimmed">
                Проект {trainingIndex + 1} из {trainingProjects.length}
              </Text>
              <Group gap="xs">
                {!trainingProjects[trainingIndex].block_if_due ? (
                  <Button
                    variant="default"
                    onClick={() => {
                      setTrainingProjects([])
                      setTrainingIndex(0)
                      setTrainingAnswers({})
                    }}
                  >
                    Закрыть
                  </Button>
                ) : null}
                <Button loading={trainingBusy} onClick={() => void submitTrainingProject()}>
                  Отправить ответы
                </Button>
              </Group>
            </Group>
          </Stack>
        ) : null}
      </Modal>
    </ShiftContext.Provider>
  )
}

/**
 * Одно состояние смены на всё приложение (шапка, дашборд, табель).
 * @param _tickMs оставлен для совместимости вызовов; тик отображения фиксирован в провайдере.
 */
export function useSidebarShift(
  _tickMs?: number,
  options?: { onMutationSuccess?: () => void },
): SidebarShiftValue {
  const ctx = useContext(ShiftContext)
  if (!ctx) {
    throw new Error('useSidebarShift must be used within ShiftProvider')
  }
  const { subscribeMutationSuccess, ...rest } = ctx
  const cb = options?.onMutationSuccess
  useEffect(() => {
    if (!cb) return
    return subscribeMutationSuccess(cb)
  }, [cb, subscribeMutationSuccess])
  return rest
}
